// pages/setup/components/BlockEditor.tsx
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from './Switch';
import { TagInput } from './TagInput';
import { Tooltip } from './Tooltip';

interface BlockEditorProps {
    blockName: string;
    config: any;
    rewards?: any[];
    onUpdate: (field: string, value: any) => void;
}

export function BlockEditor({ blockName, config, rewards, onUpdate }: BlockEditorProps) {
    const renderCommonFields = () => (
        <div className="space-y-2.5">
            <div className="space-y-1.5">
                <Label className="flex items-center text-xs">
                    Nome Customizado
                    <Tooltip text="Nome diferente do bloco padrão (use apenas se necessário)" />
                </Label>
                <Input
                    value={config.customName}
                    onChange={(e) => onUpdate('customName', e.target.value)}
                    placeholder="Deixe vazio para usar o padrão"
                    className="h-8 text-xs"
                />
            </div>

            <div className="space-y-1.5">
                <Label className="flex items-center text-xs">
                    Incluir Apenas em
                    <Tooltip text="Paths específicos onde o bloco será exibido. Ex: /videos-virais, /noticias" />
                </Label>
                <TagInput
                    tags={config.include_in}
                    onChange={(tags) => onUpdate('include_in', tags)}
                    placeholder="Digite o path e pressione Enter (ex: /videos-virais)"
                />
            </div>

            <div className="space-y-1.5">
                <Label className="flex items-center text-xs">
                    Excluir de
                    <Tooltip text="Paths onde o bloco NÃO será exibido. Ex: /checkout, /admin" />
                </Label>
                <TagInput
                    tags={config.exclude_from}
                    onChange={(tags) => onUpdate('exclude_from', tags)}
                    placeholder="Digite o path e pressione Enter (ex: /checkout)"
                />
            </div>
        </div>
    );

    const renderDisplayBlockFields = () => (
        <div className="space-y-2.5">
            <div className="grid grid-cols-2 gap-2.5">
                <div className="space-y-1.5">
                    <Label className="flex items-center text-xs">
                        Referência HTML
                        <Tooltip text="Elemento HTML usado como referência para posicionar o bloco (ex: p, div, article)" />
                    </Label>
                    <Input
                        value={config.reference}
                        onChange={(e) => onUpdate('reference', e.target.value)}
                        placeholder="p"
                        className="h-8 text-xs"
                    />
                </div>

                <div className="space-y-1.5">
                    <Label className="flex items-center text-xs">
                        Posição Container
                        <Tooltip text="Qual elemento contar (ex: se for 2, posiciona no segundo elemento <p>)" />
                    </Label>
                    <Input
                        type="number"
                        min="1"
                        value={config.insideContainer}
                        onChange={(e) => onUpdate('insideContainer', parseInt(e.target.value))}
                        className="h-8 text-xs"
                    />
                </div>
            </div>

            <div className="space-y-1.5">
                <Label className="flex items-center text-xs">
                    Posicionamento
                    <Tooltip text="Se o bloco aparece antes ou depois do elemento de referência" />
                </Label>
                <select
                    value={config.positionReference}
                    onChange={(e) => onUpdate('positionReference', e.target.value)}
                    className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                >
                    <option value="before">Antes</option>
                    <option value="after">Depois</option>
                </select>
            </div>

            {blockName === 'Topo' && 'waitReward' in config && (
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <Label className="text-xs font-medium flex items-center">
                        Aguardar Offerwall
                        <Tooltip text="Não compra blocos para não concorrer com o offerwall durante o leilão" />
                    </Label>
                    <Switch
                        checked={config.waitReward === 1}
                        onCheckedChange={(checked) => onUpdate('waitReward', checked ? 1 : 0)}
                    />
                </div>
            )}
        </div>
    );

    const renderFixedAnchorFields = () => (
        <div className="space-y-1.5">
            <Label className="flex items-center text-xs">
                Posição Fixa
                <Tooltip text="Define se o anúncio fica fixo no topo ou rodapé da página" />
            </Label>
            <select
                value={config.fixedPosition}
                onChange={(e) => onUpdate('fixedPosition', e.target.value)}
                className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
            >
                <option value="top">Topo</option>
                <option value="bottom">Rodapé</option>
            </select>
        </div>
    );

    const renderRewardFields = () => (
        <div className="space-y-2.5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <div className="space-y-1.5">
                    <Label className="flex items-center text-xs">
                        Offerwall
                        <Tooltip text="Identificador único do reward configurado" />
                    </Label>
                    <select
                        value={
                            config.reward_id &&
                            rewards?.some((r: any) => r.id === parseInt(config.reward_id))
                                ? config.reward_id
                                : ''
                        }
                        onChange={(e) => onUpdate('reward_id', e.target.value)}
                        className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                    >
                        <option value="">Escolher reward</option>
                        {rewards && rewards.length > 0 &&
                            rewards.map((reward: any) => (
                                <option key={reward.id} value={reward.id}>
                                    {reward.title || `Reward ${reward.id}`}
                                </option>
                            ))
                        }
                    </select>
                </div>
                <div className="space-y-1.5">
                    <Label className="flex items-center text-xs">
                        Frequência (minutos)
                        <Tooltip text="Intervalo em minutos entre solicitações do reward" />
                    </Label>
                    <Input
                        type="number"
                        min="1"
                        value={config.frequencyPerSession}
                        onChange={(e) => onUpdate('frequencyPerSession', parseInt(e.target.value))}
                        className="h-8 text-xs"
                    />
                </div>
            </div>
        </div>
    );

    const renderInterstitialFields = () => (
        <div className="space-y-2.5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {blockName === 'trigger' && (
                    <div className="space-y-1.5">
                        <Label className="flex items-center text-xs">
                            Delay (ms)
                            <Tooltip text="Tempo após voltar à página para exibir o anúncio" />
                        </Label>
                        <Input
                            type="number"
                            min="0"
                            step="100"
                            value={config.triggerDelay}
                            onChange={(e) => onUpdate('triggerDelay', parseInt(e.target.value))}
                            className="h-8 text-xs"
                        />
                    </div>
                )}
                <div className="space-y-1.5">
                    <Label className="flex items-center text-xs">
                        Frequência (minutos)
                        <Tooltip text="Intervalo em minutos entre solicitações" />
                    </Label>
                    <Input
                        type="number"
                        min="1"
                        value={config.frequencyPerSession}
                        onChange={(e) => onUpdate('frequencyPerSession', parseInt(e.target.value))}
                        className="h-8 text-xs"
                    />
                </div>
            </div>

        </div>
    );

    return (
        <div className="space-y-3">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                <Label className="text-xs font-medium">
                    Ativar {blockName === 'reward' ? 'offerWall' : blockName}
                </Label>
                <Switch
                    checked={config.enabled}
                    onCheckedChange={(checked) => onUpdate('enabled', checked)}
                />
            </div>

            {config.enabled && (
                <>
                    {/* Display Blocks */}
                    {['Topo', 'Content1', 'Content2'].includes(blockName) && renderDisplayBlockFields()}

                    {/* Fixed/Anchor Blocks */}
                    {['fixed', 'anchor'].includes(blockName) && renderFixedAnchorFields()}

                    {/* Reward */}
                    {blockName === 'reward' && renderRewardFields()}

                    {/* Interstitial/Trigger */}
                    {['interstitial', 'trigger'].includes(blockName) && renderInterstitialFields()}

                    {/* Common Fields */}
                    <div className="pt-2.5 border-t border-gray-200">
                        {renderCommonFields()}
                    </div>
                </>
            )}
        </div>
    );
}